import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../features/authentication/store/authStore';
import { isDemoEnvironment } from '../utils/environment';

interface PublicOnlyRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Public-only route component that redirects authenticated users to a specified route.
 * Used for landing pages and other content that should only be visible to unauthenticated users.
 *
 * Properly waits for auth hydration before making redirect decisions to prevent
 * flashing of landing page content for authenticated users.
 *
 * In demo environment, unauthenticated users are redirected to login instead of showing landing page.
 *
 * @param children - The content to render for unauthenticated users
 * @param redirectTo - Where to redirect authenticated users (default: /dashboard)
 */
export const PublicOnlyRoute: React.FC<PublicOnlyRouteProps> = ({
  children,
  redirectTo = '/app/dashboard',
}) => {
  const { isAuthenticated, isLoading, hasHydrated } = useAuthStore();
  const location = useLocation();

  // Memoize environment check to avoid repeated access on every render
  const isDemo = useMemo(() => isDemoEnvironment(), []);

  // Show loading while auth is being initialized OR while waiting for hydration
  if (isLoading || !hasHydrated()) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: 'background.default' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to dashboard (or specified route) if authenticated
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // In demo environment, redirect unauthenticated users from root to login
  // Allow access to other public pages like /demo, /about, /privacy, /terms
  if (isDemo && location.pathname === '/') {
    return <Navigate to="/login" replace />;
  }

  // Show landing page content for unauthenticated users
  return <>{children}</>;
};
